/**
 * Translation shapes for v2 (isolated copy of the v1 DTO contracts).
 *
 * - `ILanguageTranslations` — the NESTED payload as it arrives from the API.
 * - `TranslationMap` — the FLATTENED `a_b_c → value` map used at runtime.
 * - `ITranslationsApiResponse` — the API envelope.
 */
export interface INestedTranslation {
  [key: string]: string | INestedTranslation;
}

export interface ILanguageTranslations {
  [key: string]: string | INestedTranslation;
}

export type TranslationMap = Record<string, string>;

export interface ITranslationsApiResponse {
  status: boolean;
  message: string | null;
  data: ILanguageTranslations;
}

/**
 * True when `value` has the EXACT shape of a flattened i18n key — lowercase
 * snake_case with at least one underscore and no spaces (e.g.
 * `reference_portal_common_load_failed`). Already-localized prose — Arabic, or an
 * English sentence with spaces / capitals / punctuation — never matches, so this
 * is a safe test for "is this string a raw translation KEY that must be resolved
 * before it is shown to the user?". Shared by the toast + error-normalisation
 * layers so a stray key never renders raw.
 */
export function isI18nKey(value: string): boolean {
  return /^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(value);
}

/**
 * Flatten a nested translation tree into a `parent_child` keyed map.
 * (Ported verbatim from the v1 TranslationsService.flattenTranslations.)
 */
export function flattenTranslations(data: ILanguageTranslations): TranslationMap {
  const result: TranslationMap = {};

  const walk = (obj: INestedTranslation | ILanguageTranslations, prefix = ''): void => {
    Object.keys(obj ?? {}).forEach((key) => {
      const value = obj[key];
      const fullKey = prefix ? `${prefix}_${key}` : key;

      if (typeof value === 'string') {
        result[fullKey] = value;
      } else if (value && typeof value === 'object') {
        walk(value, fullKey);
      }
    });
  };

  walk(data);
  return result;
}
